import { z } from "zod";
import { zString } from "./zod_utils.js";
/**
 * What the agent says before it has thought about anything.
 *
 * Two features that look unrelated and are the same mechanism: a **welcome
 * message** the agent opens with, and **quick scenarios** — a pre-written
 * answer to a reply the agent can predict, played the moment the caller gives
 * it. Both are text the agent will say that no model produced, which means
 * both can be synthesised once and kept.
 *
 * ## The opening is per direction; the scenarios are not
 *
 * An agent answers its own number and runs a campaign on the same prompt, and
 * the two want opposite openings — a stranger who rang you has already said
 * why, and a stranger you rang has not. So `inbound` and `outbound` each carry
 * their own mode and their own welcome, and the **control plane resolves which
 * one applies** from the grant before the snapshot is sent. The realtime
 * runtime never learns what a call direction is; it is handed one opening.
 *
 * Quick scenarios stay shared. They arm on what the AGENT said, which is the
 * same prompt either way, and splitting them would double a list nobody wants
 * to keep in two places.
 *
 * ## Why they are worth having at all
 *
 * The greeting is the highest-volume single utterance a business has: every
 * call starts with it, and today every call pays full time-to-first-byte to
 * hear it. A batch generates thousands an hour. `docs/09-latency-and-lanes.md`
 * § 7 has named pre-synthesised greetings as the cheap win since before there
 * was anywhere to put one.
 *
 * Quick scenarios are the same trick one turn later. An agent that asks "is
 * now a good time?" gets one of two answers, and the reply to each is the
 * same every call — so it can be answered with no inference and no synthesis
 * at all. `docs/batch-calling-plan.md` Phase 6, and doc 18's C5.
 *
 * ## What this is NOT
 *
 * A dialogue engine. A scenario is armed only when the agent's own last
 * utterance matches one of its question variants, it answers exactly once,
 * and anything it does not recognise falls straight through to the model. The
 * moment a cached answer could depend on a tool result or on what was said
 * three turns ago, it stops being cacheable — which is why doc 14 gates
 * *dynamic* cached replies behind tool calling and this behind nothing.
 */
/** Bare ISO 639-1, matching how `agentLanguages` is spelled everywhere else. */
const languageCodeZod = zString.min(2).max(8);
/** Per-language text. A blank entry is refused: "say nothing" and "no line" are different things. */
const linesZod = (max) => z.record(languageCodeZod, zString.min(1).max(max));
/**
 * What the filler's delay will accept.
 *
 * The floor is 200ms because anything under it fires before a fast model has
 * answered and makes every quick turn start with "one moment". The ceiling is
 * generous: past a few seconds a filler is covering a stall rather than a
 * wait, and that is a decision about the agent rather than a bound this
 * schema should make.
 */
export const FILLER_BOUNDS = {
    afterMs: { min: 200, max: 10_000 },
};
export const SPEECH_PLAN_BOUNDS = {
    /** How long after the greeting is triggered the agent opens its mouth. */
    greetingDelayMs: { min: 0, max: 5_000 },
    /**
     * How long `greet_after_caller` waits for a caller who never speaks.
     *
     * The ceiling is generous because the thing underneath it is
     * `conversationPolicy.idleTimeoutMs`, which ends the call — and an outbound
     * agent that hangs up on somebody who picked up silently is worse than one
     * that greets a second late.
     */
    greetAfterSilenceMs: { min: 0, max: 15_000 },
};
/**
 * How a call opens, from the agent's side.
 *
 * - `caller_leads` — the agent says nothing; the caller speaks and the model
 *   answers. What every agent did before any of this existed, and the default.
 * - `agent_greets` — the agent speaks the welcome as soon as the call
 *   connects. What a cold outbound line needs when you want the introduction
 *   in before anything else.
 * - `greet_after_caller` — the agent waits, and plays the welcome as its reply
 *   to the caller's FIRST utterance, with no model call. This is what a real
 *   outbound call sounds like: somebody picks up and says "hello?", and the
 *   agent introduces itself in answer to that rather than talking into a
 *   handset still travelling to an ear.
 *
 * The third is not a variant of the second. `agent_greets` is driven by a
 * timer and can open its mouth into silence; `greet_after_caller` is driven by
 * the caller and cannot.
 */
/**
 * Which way a call went.
 *
 * Lives here because this is the only thing that reads it: the direction is
 * recorded on the grant and spent immediately, choosing which opening the
 * runtime is handed. A browser test session is neither and counts as
 * `outbound` — see `realtime_grants.direction`.
 */
export const CALL_DIRECTIONS = ["inbound", "outbound"];
export const callDirectionZod = z.enum(CALL_DIRECTIONS);
export const OPENING_MODES = ["caller_leads", "agent_greets", "greet_after_caller"];
export const openingModeZod = z.enum(OPENING_MODES);
/**
 * What a `greet_after_caller` agent does about a caller who picks up and says
 * nothing at all.
 *
 * Eight seconds, and it is a guess rather than a measurement — nothing has
 * timed how long somebody waits before saying "hello?" a second time. It is
 * short enough that the line does not read as dead and long enough that a
 * slow "…hello?" still gets answered rather than talked over.
 */
export const DEFAULT_GREET_AFTER_SILENCE_MS = 8_000;
/**
 * The first thing the caller hears, and how long after its trigger.
 *
 * `ignoreSpeechBefore` is the option that looks like a detail and is not. A
 * caller who says "hello?" while the greeting is still playing would normally
 * barge in — correctly, in general — but on turn one that produces an agent
 * that abandons its own introduction to answer a word that was not a question.
 * Setting it makes the greeting uninterruptible.
 *
 * It matters MORE under `greet_after_caller`, not less: the caller has just
 * spoken, so they are the most likely person in the world to say "hello?"
 * again a beat later, straight over the introduction they triggered.
 */
export const welcomeMessageZod = z.object({
    text: linesZod(500),
    /**
     * A pause before the greeting starts, measured from whatever triggered it.
     *
     * Under `agent_greets` that trigger is the call connecting, and zero is
     * usually wrong: the carrier connects the media path a moment before the
     * callee's handset reaches their ear, so an agent speaking at t=0 has its
     * first syllable clipped. Under `greet_after_caller` the trigger is the
     * caller finishing, where zero is fine — the line is already at their ear,
     * demonstrably, because they just used it.
     */
    delayMs: z
        .number()
        .int()
        .min(SPEECH_PLAN_BOUNDS.greetingDelayMs.min)
        .max(SPEECH_PLAN_BOUNDS.greetingDelayMs.max),
    /** Whether the greeting can be barged in on. See the docblock. */
    ignoreSpeechBefore: z.boolean(),
});
/**
 * One predictable exchange, answered without a model.
 *
 * ## Arming is what keeps this safe
 *
 * A scenario is not a keyword matcher over the whole call. It is armed only
 * when the agent's OWN last utterance matches one of `questionVariants` —
 * which means the cached answer can only ever be a reply to the question it
 * was written for. Without that, "no" said at any point in a conversation
 * would trigger a decline the agent was never asking about.
 *
 * The variants are a list because a model does not say the same sentence
 * twice. They are matched loosely — normalised, and on a prefix — because an
 * exact match against generated text would arm approximately never.
 */
export const quickScenarioZod = z
    .object({
    id: zString.min(1).max(50),
    /** Which way the caller went. Two, because a cached answer is only safe where the space of replies is small. */
    decision: z.enum(["positive", "negative"]),
    /** Ways the agent might have asked. At least one — a scenario with none can never arm. */
    questionVariants: z.array(zString.min(1).max(300)).min(1).max(10),
    /** What to say, per language. At least one. */
    answers: linesZod(500),
})
    .refine((scenario) => Object.keys(scenario.answers).length > 0, "Give the scenario something to say");
/**
 * How one direction's calls open.
 *
 * ## What is NOT here any more
 *
 * `initialSilenceMs` — "how long to wait for a caller who is meant to speak
 * first". It was resolved by the realtime runtime and then read by nothing:
 * `SpeechPlan.resolve/1` put it on the struct and no line of `Session.Server`
 * or `Session.State` ever looked at it. So the builder drew an enabled,
 * saveable box that governed nothing, which is the failure `PlannedFeature`'s
 * docblock is about, except worse for not even being greyed out. Removed
 * rather than wired, because the one behaviour it plausibly described is now
 * `greetAfterSilenceMs` and means something.
 */
export const callOpeningZod = z.object({
    mode: openingModeZod,
    /**
     * Null under `caller_leads`, and required in practice under either of the
     * other two — see `openingFits`. Both of those speak this text; they differ
     * only in what triggers it.
     */
    welcome: welcomeMessageZod.nullable(),
    /**
     * How long `greet_after_caller` waits before greeting a caller who has not
     * spoken. **Zero means never** — the agent waits indefinitely and the call
     * ends on `conversationPolicy.idleTimeoutMs` like any other silent line.
     *
     * Read under `greet_after_caller` only. Stored on every opening anyway so
     * that switching modes does not reveal an empty box where a number is
     * required; nothing reads it in the other two.
     */
    greetAfterSilenceMs: z
        .number()
        .int()
        .min(SPEECH_PLAN_BOUNDS.greetAfterSilenceMs.min)
        .max(SPEECH_PLAN_BOUNDS.greetAfterSilenceMs.max),
});
/**
 * The shape this column held before migration 0071 — one opening for every
 * call, keyed `whoSpeaksFirst` — read as the two it holds now.
 *
 * Here rather than only in the migration because a rolling deploy runs both
 * releases at once, and because an agent TEMPLATE row is copied into this
 * form by a form that may be older than the row. The old plan applies to both
 * directions, which is exactly what it did: there was one opening and every
 * call got it.
 *
 * Deletable once no row and no release can produce `whoSpeaksFirst`.
 */
function liftLegacyPlan(value) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return value;
    const plan = value;
    if (!("whoSpeaksFirst" in plan))
        return value;
    const opening = {
        mode: plan.whoSpeaksFirst === "agent" ? "agent_greets" : "caller_leads",
        welcome: plan.welcome ?? null,
        greetAfterSilenceMs: DEFAULT_GREET_AFTER_SILENCE_MS,
    };
    return { inbound: opening, outbound: opening, quickScenarios: plan.quickScenarios ?? [] };
}
/**
 * A line spoken into the gap while a reply is being composed.
 *
 * **Not a turn.** The runtime writes it to the synthesiser ahead of the
 * model's own text on the SAME turn, so the caller hears "one moment" and then
 * the answer as one continuous utterance. It therefore does not appear in the
 * transcript and can be barged in on exactly like any reply audio.
 */
export const fillerZod = z.object({
    text: linesZod(200),
    /**
     * How long the reply may take before the filler is spoken, measured from
     * the moment the model was asked.
     *
     * Too low and the agent talks over its own answer on every fast turn; too
     * high and it covers nothing. The runtime speaks it only if the turn has
     * still produced no audio when this expires, so a fast reply cancels it
     * without either side having to know.
     */
    afterMs: z.number().int().min(FILLER_BOUNDS.afterMs.min).max(FILLER_BOUNDS.afterMs.max),
});
export const agentSpeechPlanZod = z.preprocess(liftLegacyPlan, z.object({
    /** How a call somebody made TO this agent's number opens. */
    inbound: callOpeningZod,
    /** How a call this agent placed opens. */
    outbound: callOpeningZod,
    /**
     * Shared by both directions deliberately. A scenario arms on what the
     * AGENT said, which comes from the same prompt either way.
     */
    quickScenarios: z.array(quickScenarioZod).max(20),
    /**
     * What the agent says while the model is still writing.
     *
     * Shared for the same reason scenarios are: it answers a WAIT, and a
     * wait is the same shape whoever placed the call.
     */
    filler: fillerZod.nullable().default(null),
}));
/** Waits for the caller and says nothing first — what every agent did before any of this. */
export const CALL_OPENING_DEFAULTS = {
    mode: "caller_leads",
    welcome: null,
    greetAfterSilenceMs: DEFAULT_GREET_AFTER_SILENCE_MS,
};
/** What an agent created from scratch does, in both directions. */
export const SPEECH_PLAN_DEFAULTS = {
    inbound: CALL_OPENING_DEFAULTS,
    outbound: CALL_OPENING_DEFAULTS,
    quickScenarios: [],
    // Off, like every other thing this agent might say that nobody wrote.
    filler: null,
};
/**
 * Whether an opening that is meant to speak has anything to say.
 *
 * A plain function rather than a zod refinement because the builder needs to
 * say it under the control while somebody is still typing, and the router
 * needs to refuse it — one definition, two callers, the same rule as
 * `reengagementFits`.
 *
 * **The failure is silent by construction**: an agent set to greet with no
 * greeting is a call that connects and says nothing, which is
 * indistinguishable from a broken line, and the caller hangs up before anybody
 * finds out. Under `greet_after_caller` it is worse, because the caller has
 * already spoken into it — they say "hello?" and get silence back, which reads
 * as being hung up on.
 */
export function openingFits(opening) {
    if (!opening || opening.mode === "caller_leads")
        return { ok: true };
    if (!opening.welcome || Object.keys(opening.welcome.text).length === 0) {
        return {
            ok: false,
            message: opening.mode === "agent_greets"
                ? "This opening speaks first but has no welcome message, so the call would connect " +
                    "and go silent. Write one, or let the caller lead."
                : "This opening answers the caller with a welcome message and there isn't one, so " +
                    "they would say hello into silence. Write one, or let the caller lead.",
        };
    }
    return { ok: true };
}
/**
 * Both directions, with the failing one named.
 *
 * Named rather than merely reported, because the two openings are edited on
 * the same screen and "no welcome message" is otherwise a complaint about one
 * of two identical-looking cards.
 */
export function speechPlanFits(plan) {
    if (!plan)
        return { ok: true };
    for (const direction of ["outbound", "inbound"]) {
        const fit = openingFits(plan[direction]);
        if (!fit.ok)
            return { ok: false, message: `${directionLabel(direction)}: ${fit.message}` };
    }
    return { ok: true };
}
/** What a direction is called on screen and in an error somebody has to act on. */
export function directionLabel(direction) {
    return direction === "inbound" ? "Inbound calls" : "Outbound calls";
}
/**
 * How a question variant and an agent utterance are compared.
 *
 * Exported because **both planes have to agree on it** — the builder shows
 * somebody whether their variant would match, and the Elixir runtime does the
 * matching — and two implementations of "normalise this sentence" is two
 * implementations that will disagree about an apostrophe.
 *
 * Lowercase, strip everything that is not a letter, a digit, a combining mark
 * or a space, collapse runs of space. Deliberately blunt: a model does not say
 * the same sentence twice, and anything cleverer starts making judgements
 * about meaning that a prefix comparison cannot defend.
 *
 * **`\p{M}` is in the class and is not optional.** A Devanagari matra is a
 * combining mark, category `Mn`/`Mc`, and `\p{L}` does not match it — so a
 * class of letters and digits alone turns "क्या आप ठीक हैं" into
 * "क य आप ठ क ह", which is not the sentence and matches things it should not.
 * This repository has made that exact mistake once before, in the harness's
 * `Score.words/1`, where it silently inflated every Hindi WER figure.
 */
export function normaliseForMatch(text) {
    return text
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\p{M}\s]/gu, " ")
        .replace(/\s+/g, " ")
        .trim();
}
/**
 * How much of a variant has to appear for a scenario to arm.
 *
 * A prefix rather than an equality, because the agent's utterance is generated
 * and will carry a preamble ("Great — is now a good time?"). A prefix rather
 * than a substring-anywhere, because "no" appearing inside a longer sentence
 * is how a scenario arms on a question nobody asked.
 *
 * The number is a floor on the VARIANT's length, not a similarity score:
 * matching is exact, on a normalised string, and this only refuses to arm on a
 * variant so short that it would match half the conversation.
 */
export const MIN_VARIANT_CHARS = 8;
//# sourceMappingURL=speech_plan.zod.js.map